/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import { useCheckBreakpoint, DropDownModal, Button, InputTextField } from '@marriott/mi-ui-library';

import { StyledMiniSearchBar, StyledPortalDropdown, MobileSearchFormGlobalStyles } from './MiniSearchBar.styles';
import {
  MiniSearchBarActivePanel,
  MiniSearchBarProps,
  LlmMetadata as _LlmMetadata,
  NLSSearchTriggerParams,
  DEFAULT_MODEL,
} from './MiniSearchBar.types';
import { AI_SEARCH_TRACK_LABELS } from '../../constants/aiSearchTrackLabels';
import { NGDatepicker } from '../../molecules/NGDatepicker';
import { NGRoomsAndGuests } from '../../molecules/NGRoomsAndGuests';
import { NGSpecialRatesAndPoints } from '../../molecules/NGSpecialRatesAndPoints';
import { useSearchFormStore } from '../../store/searchFormStore';
import { StaticDataContextProvider } from '../../context/StaticDataContext';
import { EEO_MEMBER_CODES } from '../../molecules/MPropertyPriceSection/MPropertyPriceSection.constants';
import moment from 'moment';

/**
 * MiniSearchBar — compact pill-style search bar for map/list views.
 *
 * Shows: Dates | Rooms & Guests | Rate
 * Clicking a segment opens the corresponding NG molecule dropdown inline.
 * Opens states re-use the same NGDatepicker / NGRoomsAndGuests / NGSpecialRatesAndPoints
 * molecules already powering NGSearchForm.
 */

export const MiniSearchBar: FC<MiniSearchBarProps> = ({
  model = {},
  onSearch,
  onNLSSearch,
  className,
  isLoading = false,
  llmMetadata,
  currentQuery
}) => {
  // Merge AEM model on top of English defaults so nothing renders as blank/undefined
  const mergedModel = { ...DEFAULT_MODEL, ...model };

  const [activePanel, setActivePanel] = useState<MiniSearchBarActivePanel>(null);
  const [isWeekendButtonSelected, setIsWeekendButtonSelected] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLDivElement>(null);
  const dateSegRef = useRef<HTMLButtonElement>(null);
  const findHotelsButtonRef = useRef<HTMLButtonElement>(null);
  const isDesktop = useCheckBreakpoint('viewportL');

  // ── Mobile state management ───────────────────────────────────────────────
  // mobileFormModalOpen: controls the intermediate review modal
  // mobileActiveComponent: which panel is open ('dates'|'rooms'|'rate'|'form'|null)
  // openedFromFormRef: whether the panel was opened from the form modal (for back-nav)
  const [mobileFormModalOpen, setMobileFormModalOpen] = useState(false);
  const [mobileActiveComponent, setMobileActiveComponent] = useState<'dates' | 'rooms' | 'rate' | 'form' | null>(null);
  const openedFromFormRef = useRef(false);

  // ── Portal position — calculated synchronously on open ────────────────────
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left?: number;
    right?: number;
    maxHeight: number;
  } | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  // Calendar width matches the CSS override in StyledPortalDropdown for the dates panel
  const CALENDAR_WIDTH = 712;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // ── Calculate position for a given panel (pure function, no state) ────────
  const calculatePosition = useCallback(
    (panel: MiniSearchBarActivePanel): { top: number; left?: number; right?: number; maxHeight: number } | null => {
      if (!panel || !pillRef.current) return null;

      const pillRect = pillRef.current.getBoundingClientRect();
      const gap = 8;
      const viewportH = window.innerHeight;

      // Estimated intrinsic heights — used only for flip detection,
      // not for capping (CSS max-height handles the actual cap).
      const estimatedH = panel === 'dates' ? 480 : panel === 'rooms' ? 380 : 440;

      const spaceBelow = viewportH - pillRect.bottom - gap;
      const spaceAbove = pillRect.top - gap;

      // Flip upward when there's more usable space above than below
      // and the panel would otherwise be clipped below.
      const openUpward = spaceBelow < estimatedH && spaceAbove > spaceBelow;

      const top = openUpward ? Math.max(gap, pillRect.top - gap - estimatedH) : pillRect.bottom + gap;

      // Hard limit so CSS doesn't need to recalculate — whichever direction
      // the panel opens, it must not exceed the available space in that direction.
      const maxHeight = openUpward
        ? Math.min(spaceAbove, viewportH - gap * 2)
        : Math.min(spaceBelow, viewportH - gap * 2);

      if (panel === 'dates') {
        // Center calendar under the dates button
        const segRect = dateSegRef.current?.getBoundingClientRect() ?? pillRect;
        const segCentre = segRect.left + segRect.width / 2;
        const minLeft = 8;
        const maxLeft = window.innerWidth - CALENDAR_WIDTH - 8;
        const left = Math.min(Math.max(segCentre - CALENDAR_WIDTH / 2, minLeft), maxLeft);
        return { top, left, maxHeight };
      } else if (panel === 'rate') {
        // Align rate dropdown to right edge of pill
        return { top, right: window.innerWidth - pillRect.right, maxHeight };
      } else {
        // Rooms dropdown aligns to left edge of pill
        return { top, left: pillRect.left, maxHeight };
      }
    },
    []
  );

  // ── Update position on resize/scroll only when panel is open ──────────────
  useEffect(() => {
    if (!activePanel) return;

    const handleReposition = () => {
      const pos = calculatePosition(activePanel);
      if (pos) setDropdownPos(pos);
    };

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [activePanel, calculatePosition]);

  // ── Read formatted values from Zustand store ────────────────────────────
  // Use specific selectors to ensure proper re-renders when values change
  const datesFromDate = useSearchFormStore((s: any) => s.dates?.fromDate);
  const datesToDate = useSearchFormStore((s: any) => s.dates?.toDate);
  const datesFlexible = useSearchFormStore((s: any) => s.dates?.flexible);
  const datesNumberOfNights = useSearchFormStore((s: any) => s.dates?.numberOfNights);
  const datesLengthOfStay = useSearchFormStore((s: any) => s.dates?.lengthOfStay);

  const roomsNumRooms = useSearchFormStore((s: any) => s.roomsAndGuests?.numRooms);
  const roomsNumAdultsPerRoom = useSearchFormStore((s: any) => s.roomsAndGuests?.numAdultsPerRoom);
  const roomsNumChildrenPerRoom = useSearchFormStore((s: any) => s.roomsAndGuests?.numChildrenPerRoom);

  const specialRatesClusterCode = useSearchFormStore((s: any) => s.specialRates?.clusterCode);
  const specialRatesCorporateCode = useSearchFormStore((s: any) => s.specialRates?.corporateCode);
  const specialRatesGroupCode = useSearchFormStore((s: any) => s.specialRates?.groupCode);
  const specialRatesCode = useSearchFormStore((s: any) => s.specialRates?.specialRateCode);
  const usePointsRewardsRedemption = useSearchFormStore((s: any) => s.usePoints?.rewardsRedemption);


  const setActivePopup = useSearchFormStore((s: any) => s.setActivePopup);

  // Reconstruct objects for easier use in callbacks
  const dates = {
    fromDate: datesFromDate,
    toDate: datesToDate,
    flexible: datesFlexible,
    numberOfNights: datesNumberOfNights,
    lengthOfStay: datesLengthOfStay,
  };

  const roomsAndGuests = {
    numRooms: roomsNumRooms,
    numAdultsPerRoom: roomsNumAdultsPerRoom,
    numChildrenPerRoom: roomsNumChildrenPerRoom,
  };

  // ── Computed display labels ─────────────────────────────────────────────
  // Store dates take priority (user selections), then fall back to llmMetadata (BE response)
  const llmDateInfo = llmMetadata?.llmInfo?.date;
  // isFlexDate=true means the TIP SSE payload flagged this as a flexible search.
  // We use it as the authoritative flex signal alongside the store's flexible field.
  const isFlexDateFromMeta = llmMetadata?.isFlexDate === true;

  const datesLabel = (() => {
    // Priority 1: Use store dates if user has selected dates
    const from = dates?.fromDate;
    const to = dates?.toDate;
    const isFlexible = dates?.flexible === 'true' || dates?.flexible === true;
    const numberOfNights = dates?.numberOfNights;

    // Check if store has valid dates (not just the default today/tomorrow)
    if (from) {
      // Flexible dates from store: show "X nights in Month Year" format
      if (isFlexible) {
        const monthYear = moment(from).format('MMMM YYYY');
        const nights = numberOfNights || 1;
        const nightLabel = nights === 1 ? mergedModel?.nightLabel || 'night' : mergedModel?.nights || 'nights';
        return `${nights} ${nightLabel} in ${monthYear}`;
      }

      // Exact dates from store
      if (to && !moment(from).isSame(to, 'day')) {
        const fromMoment = moment(from);
        const toMoment = moment(to);

        // Same month: "Mar 21 - 28"
        if (fromMoment.isSame(toMoment, 'month')) {
          return `${fromMoment.format('MMM D')} - ${toMoment.format('D')}`;
        }
        // Different months: "Mar 28 - Apr 2"
        return `${fromMoment.format('MMM D')} - ${toMoment.format('MMM D')}`;
      }

      // Only check-in date selected
      return moment(from).format('MMM D');
    }

    // Priority 2: Fall back to llmMetadata if available (from BE response)
    if (llmDateInfo) {
      const { earliest, latest, length, type } = llmDateInfo;

      // Flexible dates: show "X nights in Month" format.
      // Treat isFlexDate=true OR type FLEXIBLE/FLEX as flexible.
      const isLlmFlex = isFlexDateFromMeta || type === 'FLEXIBLE' || type === 'FLEX';
      if (isLlmFlex && length && earliest) {
        const monthName = moment(earliest).format('MMMM YYYY');
        const nightLabel = length === 1 ? mergedModel?.nightLabel || 'night' : mergedModel?.nights || 'nights';
        return `${length} ${nightLabel} in ${monthName}`;
      }

      // Exact dates: show "Month D - D" format
      if (earliest && latest) {
        const fromMoment = moment(earliest);
        const toMoment = moment(latest);

        // Same month: "Mar 21 - 28"
        if (fromMoment.isSame(toMoment, 'month')) {
          return `${fromMoment.format('MMM D')} - ${toMoment.format('D')}`;
        }
        // Different months: "Mar 28 - Apr 2"
        return `${fromMoment.format('MMM D')} - ${toMoment.format('MMM D')}`;
      }

      // Only earliest date available
      if (earliest) {
        return moment(earliest).format('MMM D');
      }
    }

    // Default placeholder
    return mergedModel?.checkInDatePlaceholderText || 'Dates';
  })();

  const datesHasValue = Boolean(dates?.fromDate || llmDateInfo?.earliest);

  const roomsLabel = (() => {
    const count = roomsAndGuests?.numRooms ?? '1';
    const adults = roomsAndGuests?.numAdultsPerRoom ?? 1;
    const children = roomsAndGuests?.numChildrenPerRoom ?? 0;
    const totalGuests = adults + children;
    const modelRooms = parseInt(count, 10) === 1 ? mergedModel?.room || 'Room' : mergedModel?.rooms || 'Rooms';
    const modelGuests = totalGuests === 1 ? mergedModel?.guest || 'Guest' : mergedModel?.guests || 'Guests';
    return `${count} ${modelRooms}, ${totalGuests} ${modelGuests}`;
  })();

  const rateLabel = (() => {
    // "Use Points/Awards" selected — show that label in the pill
    if (usePointsRewardsRedemption) {
      return mergedModel?.usePointsCertificates || 'Use Points/Awards';
    }
    // Suppress EEO member codes (E0P, E0S, E0G) — show "Lowest Regular Rate" instead
    if (specialRatesCode && EEO_MEMBER_CODES.includes(specialRatesCode)) {
      return 'Lowest Regular Rate';
    }
    // Corp/Promo code: show the actual entered code value (not the generic label)
    if (specialRatesCode?.toLowerCase() === 'corp' && specialRatesCorporateCode) {
      return specialRatesCorporateCode;
    }
    if (specialRatesCode === 'group' && specialRatesGroupCode) {
      return specialRatesGroupCode;
    }
    // Named rates (AAA, senior, govt, …): look up the human-readable label from the
    // model's specialRatesListItems so we display e.g. "Senior Discount" not "S9R"
    if (specialRatesCode && specialRatesCode !== 'none') {
      const rateItems: Array<{ specialRatesCode: string; specialRateslabel: string }> =
        mergedModel?.specialRatesListItems ?? [];
      const matched = rateItems.find(item => item.specialRatesCode.toLowerCase() === specialRatesCode.toLowerCase());
      if (matched?.specialRateslabel) {
        return matched.specialRateslabel;
      }
    }
    // Default: show "Lowest Regular Rate"
    return 'Lowest Regular Rate';
  })();

  // Rate always has a value (minimum is "Lowest Regular Rate"), always render as selected
  const rateHasValue = true;

  // ── Toggle panel — different behavior for mobile vs desktop ────────────
  const togglePanel = (panel: MiniSearchBarActivePanel) => {
    if (!isDesktop) {
      // Mobile: Open the specific component's modal directly (like shop component)
      // After user clicks Apply in that component, it will transition to full form modal
      if (panel === 'dates') {
        setMobileActiveComponent('dates');
        setActivePopup('datepicker');
      } else if (panel === 'rooms') {
        setMobileActiveComponent('rooms');
        setActivePopup('roomsAndGuests');
      } else if (panel === 'rate') {
        setMobileActiveComponent('rate');
        setActivePopup('specialRates');
      }
      return;
    }

    // Desktop: Original behavior with portal dropdowns
    if (activePanel === panel) {
      setActivePanel(null);
      setDropdownPos(null);
      setActivePopup(null);
    } else {
      const pos = calculatePosition(panel);
      setDropdownPos(pos);
      setActivePanel(panel);

      if (panel === 'dates') {
        setActivePopup('datepicker');
      } else if (panel === 'rooms') {
        setActivePopup('roomsAndGuests');
      } else if (panel === 'rate') {
        setActivePopup('specialRates');
      }
    }
  };

  // ── Mobile: Apply in component modal → show intermediate form modal ────────
  const handleMobileComponentApply = useCallback(() => {
    openedFromFormRef.current = false;
    setMobileActiveComponent('form');
    setMobileFormModalOpen(true);
    setActivePopup(null);
  }, [setActivePopup]);

  // ── Mobile: Cancel in component modal — back to form if opened from it ────
  const handleMobileComponentCancel = useCallback(() => {
    if (openedFromFormRef.current) {
      setMobileActiveComponent('form');
      openedFromFormRef.current = false;
    } else {
      setMobileFormModalOpen(false);
      setMobileActiveComponent(null);
    }
    setActivePopup(null);
  }, [setActivePopup]);

  // ── Mobile: Tap a field row in the form modal → open that component modal ─
  const handleMobileFormFieldClick = useCallback(
    (component: 'dates' | 'rooms' | 'rate') => {
      openedFromFormRef.current = true;
      setMobileActiveComponent(component);
      if (component === 'dates') setActivePopup('datepicker');
      else if (component === 'rooms') setActivePopup('roomsAndGuests');
      else if (component === 'rate') setActivePopup('specialRates');
    },
    [setActivePopup]
  );

  // ── Mobile: Close the form modal entirely ────────────────────────────────
  const handleMobileFormClose = useCallback(() => {
    setMobileFormModalOpen(false);
    setMobileActiveComponent(null);
    setActivePopup(null);
  }, [setActivePopup]);

  // ── Mobile: Search button in form modal ──────────────────────────────────
  const handleFindHotelsClick = useCallback(() => {
    setMobileFormModalOpen(false);
    setMobileActiveComponent(null);
    setActivePopup(null);

    if (onNLSSearch) {
      const nlsParams: NLSSearchTriggerParams = {
        triggerType: 'filters',
        dates: {
          checkin: datesFromDate ? moment(datesFromDate).format('YYYY-MM-DD') : undefined,
          checkout: datesToDate ? moment(datesToDate).format('YYYY-MM-DD') : undefined,
          flexible: datesFlexible,
          lengthOfStay: datesLengthOfStay,
          numberOfNights: datesNumberOfNights,
        },
        roomsAndGuests: {
          roomsCount: roomsNumRooms,
          adultsCount: roomsNumAdultsPerRoom,
          childrenCount: roomsNumChildrenPerRoom,
        },
        specialRates: {
          clusterCode: specialRatesClusterCode,
          specialRateCode: specialRatesCode,
          corporateCode: specialRatesCorporateCode,
          groupCode: specialRatesGroupCode,
        },
        baseQueryRewrite: currentQuery || llmMetadata?.payload?.query || llmMetadata?.llmInfo?.queryRewrite,
      };
      onNLSSearch(nlsParams);
    }
  }, [
    setActivePopup,
    onNLSSearch,
    llmMetadata,
    currentQuery,
    datesFromDate,
    datesToDate,
    datesFlexible,
    datesLengthOfStay,
    datesNumberOfNights,
    roomsNumRooms,
    roomsNumAdultsPerRoom,
    roomsNumChildrenPerRoom,
    specialRatesClusterCode,
    specialRatesCode,
    specialRatesCorporateCode,
    specialRatesGroupCode,
  ]);

  // ── Close on outside click — must also check the portal dropdown ──────────
  const portalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!activePanel) return;
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inPill = rootRef.current?.contains(target);
      const inPortal = portalRef.current?.contains(target);
      if (!inPill && !inPortal) {
        setActivePanel(null);
        setActivePopup(null);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [activePanel, setActivePopup]);

  // ── Close on Escape ─────────────────────────────────────────────────────
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActivePanel(null);
        setActivePopup(null);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [activePanel, setActivePopup]);

  const handleDatesApply = useCallback(() => {
    setActivePanel(null);
    setActivePopup(null);

    const datesParams = {
      checkin: datesFromDate ? moment(datesFromDate).format('YYYY-MM-DD') : undefined,
      checkout: datesToDate ? moment(datesToDate).format('YYYY-MM-DD') : undefined,
      flexible: datesFlexible,
      lengthOfStay: datesLengthOfStay,
      numberOfNights: datesNumberOfNights,
    };
    onSearch?.({ dates: datesParams });

    if (onNLSSearch) {
      onNLSSearch({
        triggerType: 'filters',
        dates: datesParams,
        roomsAndGuests: {
          roomsCount: roomsNumRooms,
          adultsCount: roomsNumAdultsPerRoom,
          childrenCount: roomsNumChildrenPerRoom,
        },
        specialRates: {
          clusterCode: specialRatesClusterCode,
          specialRateCode: specialRatesCode,
          corporateCode: specialRatesCorporateCode,
          groupCode: specialRatesGroupCode,
        },
        baseQueryRewrite: currentQuery || llmMetadata?.payload?.query || llmMetadata?.llmInfo?.queryRewrite,
      });
    }
  }, [
    datesFromDate,
    datesToDate,
    datesFlexible,
    datesLengthOfStay,
    datesNumberOfNights,
    onSearch,
    setActivePopup,
    onNLSSearch,
    llmMetadata,
    currentQuery,
    roomsNumRooms,
    roomsNumAdultsPerRoom,
    roomsNumChildrenPerRoom,
    specialRatesClusterCode,
    specialRatesCode,
    specialRatesCorporateCode,
    specialRatesGroupCode,
  ]);

  // Called when user clicks X to close Dates modal (cancel without applying)
  const handleDatesClose = useCallback(() => {
    setActivePanel(null);
    setActivePopup(null);
  }, [setActivePopup]);

  const handleRoomsApply = useCallback(() => {
    setActivePanel(null);
    setActivePopup(null);

    const roomsParams = {
      roomsCount: roomsNumRooms,
      adultsCount: roomsNumAdultsPerRoom,
      childrenCount: roomsNumChildrenPerRoom,
    };
    onSearch?.({ roomsAndGuests: roomsParams });

    if (onNLSSearch) {
      onNLSSearch({
        triggerType: 'filters',
        dates: {
          checkin: datesFromDate ? moment(datesFromDate).format('YYYY-MM-DD') : undefined,
          checkout: datesToDate ? moment(datesToDate).format('YYYY-MM-DD') : undefined,
          flexible: datesFlexible,
          lengthOfStay: datesLengthOfStay,
          numberOfNights: datesNumberOfNights,
        },
        roomsAndGuests: roomsParams,
        specialRates: {
          clusterCode: specialRatesClusterCode,
          specialRateCode: specialRatesCode,
          corporateCode: specialRatesCorporateCode,
          groupCode: specialRatesGroupCode,
        },
        baseQueryRewrite: currentQuery || llmMetadata?.payload?.query || llmMetadata?.llmInfo?.queryRewrite,
      });
    }
  }, [
    roomsNumRooms,
    roomsNumAdultsPerRoom,
    roomsNumChildrenPerRoom,
    onSearch,
    setActivePopup,
    onNLSSearch,
    llmMetadata,
    currentQuery,
    datesFromDate,
    datesToDate,
    datesFlexible,
    datesLengthOfStay,
    datesNumberOfNights,
    specialRatesClusterCode,
    specialRatesCode,
    specialRatesCorporateCode,
    specialRatesGroupCode,
  ]);

  // Called when user clicks X to close Rooms & Guests modal (cancel without applying)
  const handleRoomsClose = useCallback(() => {
    setActivePanel(null);
    setActivePopup(null);
  }, [setActivePopup]);

  const handleRateApply = useCallback(() => {
    setActivePanel(null);
    setActivePopup(null);

    const ratesParams = {
      clusterCode: specialRatesClusterCode,
      specialRateCode: specialRatesCode,
      corporateCode: specialRatesCorporateCode,
      groupCode: specialRatesGroupCode,
    };
    onSearch?.({ specialRates: ratesParams });

    if (onNLSSearch) {
      onNLSSearch({
        triggerType: 'filters',
        dates: {
          checkin: datesFromDate ? moment(datesFromDate).format('YYYY-MM-DD') : undefined,
          checkout: datesToDate ? moment(datesToDate).format('YYYY-MM-DD') : undefined,
          flexible: datesFlexible,
          lengthOfStay: datesLengthOfStay,
          numberOfNights: datesNumberOfNights,
        },
        roomsAndGuests: {
          roomsCount: roomsNumRooms,
          adultsCount: roomsNumAdultsPerRoom,
          childrenCount: roomsNumChildrenPerRoom,
        },
        specialRates: ratesParams,
        baseQueryRewrite: currentQuery || llmMetadata?.payload?.query || llmMetadata?.llmInfo?.queryRewrite,
      });
    }
  }, [
    specialRatesClusterCode,
    specialRatesCode,
    specialRatesCorporateCode,
    specialRatesGroupCode,
    onSearch,
    setActivePopup,
    onNLSSearch,
    llmMetadata,
    currentQuery,
    datesFromDate,
    datesToDate,
    datesFlexible,
    datesLengthOfStay,
    datesNumberOfNights,
    roomsNumRooms,
    roomsNumAdultsPerRoom,
    roomsNumChildrenPerRoom,
  ]);

  // Called when user clicks X to close Rates & Points modal (cancel without applying)
  const handleRateClose = useCallback(() => {
    setActivePanel(null);
    setActivePopup(null);
  }, [setActivePopup]);

  return (
    <>
      <MobileSearchFormGlobalStyles />
      <StaticDataContextProvider value={mergedModel}>
        <StyledMiniSearchBar className={clsx('mini-search-bar', isLoading && 'is-loading', className)}>
          <div className="mini-search-bar-root p-1" ref={rootRef}>
            {/* ── Collapsed pill ─────────────────────────────────── */}
            <div className="mini-search-pill" ref={pillRef} role="group" aria-label="Search filters">
              {/* Dates segment */}
              <button
                ref={dateSegRef}
                type="button"
                className={clsx(
                  'mini-search-segment mini-search-segment--dates',
                  activePanel === 'dates' && 'is-active'
                )}
                onClick={() => !isLoading && togglePanel('dates')}
                aria-expanded={activePanel === 'dates'}
                aria-haspopup="dialog"
                aria-label="Change dates"
                title={datesLabel}
                disabled={isLoading}
                data-track-label={AI_SEARCH_TRACK_LABELS.DATE_INPUT}
              >
                {isLoading ? (
                  <span className="mini-search-skeleton-bar skeleton-loader" aria-hidden="true" />
                ) : (
                  <span className={clsx('segment-value', datesHasValue ? 'has-value' : 'no-value')}>{datesLabel}</span>
                )}
              </button>

              <span
                className={clsx('mini-search-divider', isLoading && 'mini-search-divider--hidden')}
                aria-hidden="true"
              />

              {/* Rooms & Guests segment */}
              <button
                type="button"
                className={clsx(
                  'mini-search-segment mini-search-segment--rooms',
                  activePanel === 'rooms' && 'is-active'
                )}
                onClick={() => !isLoading && togglePanel('rooms')}
                aria-expanded={activePanel === 'rooms'}
                aria-haspopup="dialog"
                aria-label="Change rooms and guests"
                title={roomsLabel}
                disabled={isLoading}
                data-track-label={AI_SEARCH_TRACK_LABELS.ROOMS_PARTY_INPUT}
              >
                {isLoading ? (
                  <span
                    className="mini-search-skeleton-bar mini-search-skeleton-bar--wide skeleton-loader"
                    aria-hidden="true"
                  />
                ) : (
                  <span className="segment-value has-value">{roomsLabel}</span>
                )}
              </button>

              <span
                className={clsx('mini-search-divider', isLoading && 'mini-search-divider--hidden')}
                aria-hidden="true"
              />

              {/* Rate segment */}
              <button
                type="button"
                className={clsx('mini-search-segment mini-search-segment--rate', activePanel === 'rate' && 'is-active')}
                onClick={() => !isLoading && togglePanel('rate')}
                aria-expanded={activePanel === 'rate'}
                aria-haspopup="dialog"
                aria-label="Change rate"
                title={rateLabel}
                disabled={isLoading}
                data-track-label={AI_SEARCH_TRACK_LABELS.RATE_INPUT}
              >
                {isLoading ? (
                  <span className="mini-search-skeleton-bar skeleton-loader" aria-hidden="true" />
                ) : (
                  <span className={clsx('segment-value', rateHasValue ? 'has-value' : 'no-value')}>{rateLabel}</span>
                )}
              </button>
            </div>
          </div>
        </StyledMiniSearchBar>
      </StaticDataContextProvider>

      {/* ── Desktop: Dropdown panels rendered via portal ── */}
      {isMounted &&
        isDesktop &&
        activePanel &&
        dropdownPos &&
        ReactDOM.createPortal(
          <StaticDataContextProvider value={mergedModel}>
            <StyledPortalDropdown
              ref={portalRef}
              $top={dropdownPos.top}
              $left={activePanel === 'rate' ? undefined : dropdownPos.left}
              $right={activePanel === 'rate' ? dropdownPos.right : undefined}
              $panel={activePanel}
              $isMobile={false}
              $maxHeight={dropdownPos.maxHeight}
            >
              {activePanel === 'dates' && (
                <div className="portal-panel portal-panel--dates">
                  <NGDatepicker
                    isWeekendButtonSelected={isWeekendButtonSelected}
                    setIsWeekendButtonSelected={setIsWeekendButtonSelected}
                    isNextGenHP={true}
                    setDefaultDate={true}
                    isModalOpen={true}
                    hideInput={true}
                    isMobileTestView={false}
                    isInModal={true}
                    onDatePickerChange={(isDatesAvailable: boolean) => {
                      if (isDatesAvailable) handleDatesApply();
                    }}
                    onDatePickerCancel={handleDatesClose}
                    hideClearCta={false}
                    applyTrackLabel={AI_SEARCH_TRACK_LABELS.APPLY}
                  />
                </div>
              )}

              {activePanel === 'rooms' && (
                <div
                  className="portal-panel portal-panel--rooms"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1 1 auto',
                    minHeight: 0,
                    overflow: 'hidden',
                  }}
                >
                  <NGRoomsAndGuests
                    isDesktopView={true}
                    isNextGenHp={true}
                    forceOpen={true}
                    isInModal={true}
                    showDesktopFooter={true}
                    onChange={handleRoomsApply}
                    onCancel={handleRoomsClose}
                    applyTrackLabel={AI_SEARCH_TRACK_LABELS.APPLY}
                  />
                </div>
              )}

              {activePanel === 'rate' && (
                <div
                  className="portal-panel portal-panel--rate"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1 1 auto',
                    minHeight: 0,
                    overflow: 'hidden',
                  }}
                >
                  <NGSpecialRatesAndPoints
                    isDesktopView={true}
                    isNextGenHP={true}
                    forceOpen={true}
                    isInModal={true}
                    showDesktopFooter={true}
                    onChange={handleRateApply}
                    onCancel={handleRateClose}
                    isSecondaryBlockEnabled={true}
                    applyTrackLabel={AI_SEARCH_TRACK_LABELS.APPLY}
                  />
                </div>
              )}
            </StyledPortalDropdown>
          </StaticDataContextProvider>,
          document.body
        )}

      {/* ── Mobile: Component-specific modals (dates/rooms/rate) ──
          Portaled to document.body (same idea as desktop dropdowns) so fixed layers
          and z-index are not trapped/clipped by iOS WebKit inside overflow/transform
          ancestors (e.g. OChat preview). NGDatepicker.styles z-index then applies at
          the root stacking context like in Chrome responsive mode. */}

      {/* Mobile: Datepicker modal */}
      {isMounted &&
        !isDesktop &&
        mobileActiveComponent === 'dates' &&
        ReactDOM.createPortal(
          <StaticDataContextProvider value={mergedModel}>
            <NGDatepicker
              isWeekendButtonSelected={isWeekendButtonSelected}
              setIsWeekendButtonSelected={setIsWeekendButtonSelected}
              isNextGenHP={true}
              setDefaultDate={true}
              isModalOpen={true}
              hideInput={true}
              isMobileTestView={true}
              isInModal={false}
              onDatePickerChange={() => handleMobileComponentApply()}
              onDatePickerCancel={handleMobileComponentCancel}
              hideClearCta={false}
              applyTrackLabel={AI_SEARCH_TRACK_LABELS.APPLY}
            />
          </StaticDataContextProvider>,
          document.body
        )}

      {/* Mobile: Rooms & Guests modal */}
      {isMounted &&
        !isDesktop &&
        mobileActiveComponent === 'rooms' &&
        ReactDOM.createPortal(
          <StaticDataContextProvider value={mergedModel}>
            <NGRoomsAndGuests
              isDesktopView={false}
              isNextGenHp={true}
              forceOpen={true}
              isInModal={false}
              onChange={handleMobileComponentApply}
              onCancel={handleMobileComponentCancel}
              applyTrackLabel={AI_SEARCH_TRACK_LABELS.APPLY}
            />
          </StaticDataContextProvider>,
          document.body
        )}

      {/* Mobile: Special Rates modal */}
      {isMounted &&
        !isDesktop &&
        mobileActiveComponent === 'rate' &&
        ReactDOM.createPortal(
          <StaticDataContextProvider value={mergedModel}>
            <NGSpecialRatesAndPoints
              isDesktopView={false}
              isNextGenHP={true}
              forceOpen={true}
              isInModal={false}
              isSecondaryBlockEnabled={true}
              onChange={handleMobileComponentApply}
              onCancel={handleMobileComponentCancel}
              applyTrackLabel={AI_SEARCH_TRACK_LABELS.APPLY}
            />
          </StaticDataContextProvider>,
          document.body
        )}

      {/* ── Mobile: Intermediate form modal — preview + Search button ── */}
      {isMounted &&
        !isDesktop &&
        mobileFormModalOpen &&
        mobileActiveComponent === 'form' &&
        ReactDOM.createPortal(
          <DropDownModal
            id="mini-search-form-modal"
            applyDefaultHeight={false}
            className="mobile-search-form standard"
            role="dialog"
            childrenClassName="search-form-dropdown"
            show={mobileFormModalOpen}
            dropdownModalOpenState={true}
            setDropdownModalOpenState={setMobileFormModalOpen}
            mobileTakeOver={true}
            mobileModalHeading={mergedModel?.placeholderTextMobile || 'Search'}
            mobileHeaderEnabled={true}
            dropdownModalOnCLoseFunc={handleMobileFormClose}
            customLastElementRef={findHotelsButtonRef}
          >
            <StaticDataContextProvider value={mergedModel}>
              <div className="mobile-form-fields">
                {/* Dates row */}
                <div
                  className="mobile-form-field"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleMobileFormFieldClick('dates')}
                  onKeyDown={e => e.key === 'Enter' && handleMobileFormFieldClick('dates')}
                >
                  <InputTextField
                    label={mergedModel?.stayDates || 'Dates'}
                    inputValue={datesLabel}
                    className="m-input-text-field"
                    readOnly
                    getInputProps={() => ({ readOnly: true })}
                    showBadge={Boolean(datesToDate && datesNumberOfNights > 0)}
                    badgeValue={
                      datesNumberOfNights > 0
                        ? `${datesNumberOfNights} ${datesNumberOfNights === 1
                          ? mergedModel?.nightLabel || 'night'
                          : mergedModel?.nights || 'nights'
                        }`
                        : undefined
                    }
                    showIcon={false}
                  />
                </div>

                {/* Rooms & Guests row */}
                <div
                  className="mobile-form-field"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleMobileFormFieldClick('rooms')}
                  onKeyDown={e => e.key === 'Enter' && handleMobileFormFieldClick('rooms')}
                >
                  <InputTextField
                    label={mergedModel?.roomsAndGuestEyebrowText || 'Rooms & Guests'}
                    inputValue={roomsLabel}
                    className="m-input-text-field"
                    readOnly
                    getInputProps={() => ({ readOnly: true })}
                    showIcon={true}
                    iconClass="icon-arrow-down"
                  />
                </div>

                {/* Rates row */}
                <div
                  className="mobile-form-field"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleMobileFormFieldClick('rate')}
                  onKeyDown={e => e.key === 'Enter' && handleMobileFormFieldClick('rate')}
                >
                  <InputTextField
                    label={mergedModel?.specialRatesEyebrowText || 'Rates & Points'}
                    inputValue={rateLabel}
                    className="m-input-text-field"
                    readOnly
                    getInputProps={() => ({ readOnly: true })}
                    showIcon={true}
                    iconClass="icon-arrow-down"
                  />
                </div>

                {/* Search CTA */}
                <div className="mobile-form-cta">
                  <Button
                    ref={findHotelsButtonRef}
                    className="m-button-primary find-hotels-btn"
                    callback={handleFindHotelsClick}
                    ariaLabel={mergedModel?.submitCTA || 'Find Hotels'}
                  >
                    <span className="icon-search" />
                    {mergedModel?.submitCTA || 'Find Hotels'}
                  </Button>
                </div>
              </div>
            </StaticDataContextProvider>
          </DropDownModal>,
          document.body
        )}
    </>
  );
};

export default MiniSearchBar;
